import React, { useCallback, useContext, useMemo } from 'react';
import { createColumnHelper } from '@tanstack/react-table';
import { Box, Link, Stack, Typography } from '@mui/material';
import { GridCellParams } from '@mui/x-data-grid-pro';

import { translate as t } from 'hooks/useTranslations';
import useUrlParams from 'hooks/useUrlParams';
import { LegacyArchivedManualsContext } from '../context';
import { QuickSearch } from 'components/_atoms';
import { ArchivedManualItem } from 'views/App/Portal/Policy/_types/manuals/draft';
import CommonLayout from 'views/App/Portal/_layouts/CommonLayout';
import GridSection, { ColumnDef } from 'components/_layouts/GridSection';
import PageToolbar from '_lex/_atoms/PageToolbar';

type LegacyArchivedManualsUrlParams = {
  q?: string | null;
};

const defaultParams: LegacyArchivedManualsUrlParams = {
  q: null,
};

const columnHelper = createColumnHelper<ArchivedManualItem>();

const LegacyArchivedManualsList: React.FC = () => {
  const { manuals, manualsQuery, params, updateParams } = useContext(LegacyArchivedManualsContext);
  const [urlParams, setUrlParams] = useUrlParams(defaultParams);

  const handleUpdateParams = useCallback(
    (updatedParams: LegacyArchivedManualsUrlParams) => {
      setUrlParams((prev: any) => ({
        ...prev,
        ...updatedParams,
      }));
      document.body.dispatchEvent(new Event('params-changed'));
    },
    [setUrlParams]
  );

  const columns: ColumnDef = useMemo(
    () => ({
      name: {
        header: t('policy.manuals.properties.name'),
        flex: 2,
        renderer: (params: GridCellParams) => {
          const row = params.row;
          if (!row) return null;
          return (
            <Stack>
              <Link href={`./${row.id}`}>
                <Typography
                  variant='h6'
                  sx={{ fontWeight: 'semibold' }}
                >
                  {row.name}
                </Typography>
              </Link>
            </Stack>
          );
        },
      },
    }),
    [t]
  );

  return (
    <CommonLayout
      headerProps={{
        title: t('policy.legacyArchivedManuals.title'),
        subtitle: t('policy.legacyArchivedManuals.subtitle'),
      }}
    >
      <Box sx={{ display: 'grid', gridTemplateRows: 'auto 1fr', gap: 4, minHeight: 0 }}>
        <PageToolbar
          leftItems={
            <QuickSearch
              id='legacy_archived_manuals_keyword_search'
              initialValue={params.q || ''}
              onChange={(val) => updateParams({ q: val })}
              placeholder={t('policy.legacyArchivedManuals.search.placeholder')}
            />
          }
        />
        <GridSection
          columns={columns}
          data={manuals}
          query={manualsQuery}
        />
      </Box>
    </CommonLayout>
  );
};

export default LegacyArchivedManualsList;
